import HighTone from "@audio/high_short.mp3";
import LowTone from "@audio/low_short.mp3";
import { Typography } from "@mui/material";
import DtgPractiseField from "@root/components/molecules/DtgPractiseField/Component";
import {
  KeyBlueDefaultIcon,
  KeyBluePressedIcon,
  KeyGreenDefaultIcon,
  KeyGreenPressedIcon,
  KeyRedDefaultIcon,
  KeyRedPressedIcon,
  KeySuccessColorIcon,
  KeyWhiteDefaultIcon,
  KeyWhitePressedIcon,
  KeyYellowDefaultIcon,
  KeyYellowPressedIcon,
  PedalLeftDefaultIcon,
  PedalLeftPressedIcon,
  PedalLeftSuccessIcon,
  PedalRightDefaultIcon,
  PedalRightPressedIcon,
  PedalRightSuccessIcon,
  SoundCorrectButtonIcon,
  SoundHighDefaultIcon,
  SoundHighPressedIcon,
  SoundLowDefaultIcon,
  SoundLowPressedIcon,
  SoundPlayingIcon,
} from "@root/icons/DtgIcons";
import React from "react";
import { useLocalStorage } from "usehooks-ts";
import style from "./style.module.css";

const PRACTISE_DELAY_MS = 2000;

interface DtgPractiseProps {
  nextFn: () => void;
}

interface SoundPractiseInitial {
  kind: "initial";
}

interface SoundPractiseLow {
  kind: "low";
}

interface SoundPractiseHigh {
  kind: "high";
}

interface SoundPractiseError {
  kind: "error";
  expected: "low" | "high";
}

interface SoundPractiseFinished {
  kind: "finished";
}

type SoundPractiseState =
  | SoundPractiseInitial
  | SoundPractiseLow
  | SoundPractiseHigh
  | SoundPractiseError
  | SoundPractiseFinished;

export const DtgSoundPractise = ({ nextFn }: DtgPractiseProps) => {
  const inputTimeoutMs = 5000;
  const [activeKey, setActiveKey] = React.useState<"low" | "high" | "idle">("idle");
  const [playingTone, setPlayingTone] = React.useState<"low" | "high" | "idle">("idle");
  const [currentState, setCurrentState] = React.useState<SoundPractiseState>({ kind: "initial" });
  const hasCalledNextRef = React.useRef<boolean>(false);
  const recoveryTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const timerId = window.setTimeout(() => setCurrentState({ kind: "low" }), PRACTISE_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, []);

  React.useEffect(() => {
    return () => {
      if (recoveryTimerRef.current !== null) window.clearTimeout(recoveryTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (currentState.kind !== "finished" || hasCalledNextRef.current) {
      return;
    }
    hasCalledNextRef.current = true;
    nextFn();
  }, [currentState.kind, nextFn]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      event.preventDefault();

      if (event.repeat) {
        return;
      }

      const isLowKey = event.key === "F6";
      const isHighKey = event.key === "F7";

      if (!isLowKey && !isHighKey) {
        return;
      }

      if (
        currentState.kind === "initial" ||
        currentState.kind === "error" ||
        currentState.kind === "finished"
      ) {
        return;
      }

      setActiveKey(isLowKey ? "low" : "high");

      if (currentState.kind === "low") {
        if (isLowKey) {
          window.setTimeout(() => {
            setCurrentState({ kind: "high" });
          }, 300);
          return;
        }
        setCurrentState({ kind: "error", expected: "low" });
        return;
      }

      if (currentState.kind === "high") {
        if (isHighKey) {
          window.setTimeout(() => {
            setPlayingTone("idle");
            setCurrentState({ kind: "finished" });
          }, 300);
          return;
        }
        setCurrentState({ kind: "error", expected: "high" });
      }
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.key === "F6" || event.key === "F7") {
        setActiveKey("idle");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [currentState.kind]);

  React.useEffect(() => {
    if (currentState.kind !== "low" && currentState.kind !== "high") {
      return;
    }

    const timerId = window.setTimeout(() => {
      setCurrentState({ kind: "error", expected: currentState.kind });
    }, inputTimeoutMs);

    return () => window.clearTimeout(timerId);
  }, [currentState.kind, inputTimeoutMs]);

  const handleErrorClose = (): void => {
    if (currentState.kind !== "error") {
      return;
    }
    const expected = currentState.expected;
    setPlayingTone("idle");
    setActiveKey("idle");
    setCurrentState({ kind: "initial" });
    if (recoveryTimerRef.current !== null) window.clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = window.setTimeout(
      () => setCurrentState({ kind: expected }),
      PRACTISE_DELAY_MS,
    );
  };

  React.useEffect(() => {
    if (currentState.kind !== "error") {
      return;
    }

    const handleEnterDismiss = (event: KeyboardEvent): void => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      handleErrorClose();
    };

    window.addEventListener("keydown", handleEnterDismiss);
    return () => {
      window.removeEventListener("keydown", handleEnterDismiss);
    };
  }, [currentState]);

  const toneSrc =
    currentState.kind === "low" ? LowTone : currentState.kind === "high" ? HighTone : null;
  const toneKind =
    currentState.kind === "low" || currentState.kind === "high" ? currentState.kind : "idle";
  const hasCompletedLow =
    currentState.kind === "high" ||
    currentState.kind === "finished" ||
    (currentState.kind === "error" && currentState.expected === "high");

  return (
    <DtgPractiseField hasError={currentState.kind === "error"} onErrorClose={handleErrorClose}>
      <Typography variant="h1" component="h1" color="var(--color-brand-primary-500)">
        Geluidsignalen
      </Typography>
      <p>Druk op de juiste toets na het horen van een lage (L) of hoge (H) toon.</p>

      <div
        className={style["playing-field-container"]}
        style={{ "--flow-padding": "6rem" } as React.CSSProperties}
      >
        <div className={style["playing-field"]}>
          {playingTone !== "idle" && <SoundPlayingIcon />}
        </div>
        <div className={style["sound-buttons-container"]}>
          {hasCompletedLow ? (
            <SoundCorrectButtonIcon />
          ) : activeKey === "low" ? (
            <SoundLowPressedIcon />
          ) : (
            <SoundLowDefaultIcon />
          )}
          {activeKey === "high" ? <SoundHighPressedIcon /> : <SoundHighDefaultIcon />}
        </div>
      </div>
      {toneSrc && (
        // biome-ignore lint/a11y/useMediaCaption: we don't use captions in this exam
        <audio
          key={currentState.kind}
          src={toneSrc}
          autoPlay
          onPlay={() => {
            setPlayingTone(toneKind);
          }}
          onEnded={() => {
            setPlayingTone("idle");
          }}
        />
      )}
    </DtgPractiseField>
  );
};

export const DtgColorsPractise = ({ nextFn }: DtgPractiseProps) => {
  const gridIconSize = 80;
  const targetIconSize = 64;
  const order = ["white", "red", "blue", "green", "yellow"] as const;
  type PractiseColor = (typeof order)[number];
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [pressedColor, setPressedColor] = React.useState<PractiseColor | null>(null);
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [targetPosition, setTargetPosition] = React.useState<{ left: number; top: number }>({
    left: 50,
    top: 50,
  });
  const isFinished = currentIndex >= order.length;
  const currentTargetColor: PractiseColor | null = isFinished ? null : order[currentIndex];

  const keyMap: Record<string, PractiseColor> = React.useMemo(
    () => ({
      F1: "white",
      F2: "blue",
      F3: "yellow",
      F4: "red",
      F5: "green",
    }),
    [],
  );

  const renderColorKey = (
    color: PractiseColor,
    variant: "default" | "pressed" | "success",
    size: number,
  ): JSX.Element => {
    if (variant === "success") {
      return <KeySuccessColorIcon width={size} height={size} />;
    }

    switch (color) {
      case "white":
        return variant === "pressed" ? (
          <KeyWhitePressedIcon width={size} height={size} />
        ) : (
          <KeyWhiteDefaultIcon width={size} height={size} />
        );
      case "red":
        return variant === "pressed" ? (
          <KeyRedPressedIcon width={size} height={size} />
        ) : (
          <KeyRedDefaultIcon width={size} height={size} />
        );
      case "blue":
        return variant === "pressed" ? (
          <KeyBluePressedIcon width={size} height={size} />
        ) : (
          <KeyBlueDefaultIcon width={size} height={size} />
        );
      case "green":
        return variant === "pressed" ? (
          <KeyGreenPressedIcon width={size} height={size} />
        ) : (
          <KeyGreenDefaultIcon width={size} height={size} />
        );
      default:
        return variant === "pressed" ? (
          <KeyYellowPressedIcon width={size} height={size} />
        ) : (
          <KeyYellowDefaultIcon width={size} height={size} />
        );
    }
  };

  React.useEffect(() => {
    if (isFinished) {
      nextFn();
    }
  }, [isFinished, nextFn]);

  React.useEffect(() => {
    if (isFinished) {
      return;
    }

    setTargetPosition({
      // Keep target away from edges so the 80x80 circle remains visible.
      left: Math.floor(Math.random() * 56) + 22,
      top: Math.floor(Math.random() * 56) + 22,
    });
  }, [currentIndex, isFinished]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      event.preventDefault();

      if (event.repeat || hasError || isFinished) {
        return;
      }

      const mappedColor = keyMap[event.key];
      if (!mappedColor) {
        return;
      }
      setPressedColor(mappedColor);

      if (mappedColor === order[currentIndex]) {
        window.setTimeout(() => {
          setPressedColor(null);
          setCurrentIndex((old) => old + 1);
        }, 180);
        return;
      }

      setHasError(true);
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (keyMap[event.key]) {
        setPressedColor(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [currentIndex, hasError, isFinished, keyMap, order]);

  const handleErrorClose = (): void => {
    setPressedColor(null);
    setHasError(false);
  };

  React.useEffect(() => {
    if (!hasError) {
      return;
    }

    const handleEnterDismiss = (event: KeyboardEvent): void => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      handleErrorClose();
    };

    window.addEventListener("keydown", handleEnterDismiss);
    return () => {
      window.removeEventListener("keydown", handleEnterDismiss);
    };
  }, [hasError]);

  const completedColors = React.useMemo(
    () => new Set<PractiseColor>(order.slice(0, currentIndex)),
    [order, currentIndex],
  );

  return (
    <DtgPractiseField hasError={hasError} onErrorClose={handleErrorClose}>
      <Typography variant="h1" component="h1" color="var(--color-brand-primary-500)">
        Kleursignalen
      </Typography>
      <p>Druk de juist gekleurde knoppen op het toetsenbord in</p>

      <div className={style["playing-field-container"]}>
        <div className={style["playing-field"]}>
          {currentTargetColor && (
            <div
              className={style["playing-field-target"]}
              style={{ left: `${targetPosition.left}%`, top: `${targetPosition.top}%` }}
            >
              {renderColorKey(
                currentTargetColor,
                pressedColor === currentTargetColor ? "pressed" : "default",
                targetIconSize,
              )}
            </div>
          )}
        </div>
        <div className={style["color-grid"]}>
          <div className={style["color-slot-white"]}>
            {renderColorKey(
              "white",
              completedColors.has("white")
                ? "success"
                : pressedColor === "white"
                  ? "pressed"
                  : "default",
              gridIconSize,
            )}
          </div>
          <div className={style["color-slot-blue"]}>
            {renderColorKey(
              "blue",
              completedColors.has("blue")
                ? "success"
                : pressedColor === "blue"
                  ? "pressed"
                  : "default",
              gridIconSize,
            )}
          </div>
          <div className={style["color-slot-yellow"]}>
            {renderColorKey(
              "yellow",
              completedColors.has("yellow")
                ? "success"
                : pressedColor === "yellow"
                  ? "pressed"
                  : "default",
              gridIconSize,
            )}
          </div>
          <div className={style["color-slot-red"]}>
            {renderColorKey(
              "red",
              completedColors.has("red")
                ? "success"
                : pressedColor === "red"
                  ? "pressed"
                  : "default",
              gridIconSize,
            )}
          </div>
          <div className={style["color-slot-green"]}>
            {renderColorKey(
              "green",
              completedColors.has("green")
                ? "success"
                : pressedColor === "green"
                  ? "pressed"
                  : "default",
              gridIconSize,
            )}
          </div>
        </div>
      </div>
    </DtgPractiseField>
  );
};

interface PedalPractiseInitial {
  kind: "initial";
}

interface PedalPractiseLeft {
  kind: "left";
}

interface PedalPractiseRight {
  kind: "right";
}

interface PedalPractiseError {
  kind: "error";
  expected: "left" | "right";
}

interface PedalPractiseFinished {
  kind: "finished";
}

type PedalPractiseState =
  | PedalPractiseInitial
  | PedalPractiseLeft
  | PedalPractiseRight
  | PedalPractiseError
  | PedalPractiseFinished;

export const DtgPedalsPractise = ({ nextFn }: DtgPractiseProps) => {
  const [isLeftPedalF8] = useLocalStorage<string | null>("isLeftPedalF8", null);
  const [activePedal, setActivePedal] = React.useState<"left" | "right" | "idle">("idle");
  const [currentState, setCurrentState] = React.useState<PedalPractiseState>({ kind: "initial" });
  const hasCalledNextRef = React.useRef<boolean>(false);
  const recoveryTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const timerId = window.setTimeout(() => setCurrentState({ kind: "left" }), PRACTISE_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, []);

  React.useEffect(() => {
    return () => {
      if (recoveryTimerRef.current !== null) window.clearTimeout(recoveryTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (currentState.kind !== "finished" || hasCalledNextRef.current) {
      return;
    }
    hasCalledNextRef.current = true;
    nextFn();
  }, [currentState.kind, nextFn]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      event.preventDefault();

      if (event.repeat) {
        return;
      }

      const isF8 = event.key === "F8";
      const isF9 = event.key === "F9";

      if (!isF8 && !isF9) {
        return;
      }

      if (
        currentState.kind === "initial" ||
        currentState.kind === "error" ||
        currentState.kind === "finished"
      ) {
        return;
      }

      const leftIsF8 = isLeftPedalF8 === "true";
      const isLeftPedal = leftIsF8 ? isF8 : isF9;
      const isRightPedal = leftIsF8 ? isF9 : isF8;
      setActivePedal(isLeftPedal ? "left" : "right");

      if (currentState.kind === "left") {
        if (isLeftPedal) {
          window.setTimeout(() => {
            setCurrentState({ kind: "right" });
          }, 300);
          return;
        }
        setCurrentState({ kind: "error", expected: "left" });
        return;
      }

      if (currentState.kind === "right") {
        if (isRightPedal) {
          window.setTimeout(() => {
            setCurrentState({ kind: "finished" });
          }, 300);
          return;
        }
        setCurrentState({ kind: "error", expected: "right" });
      }
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.key === "F8" || event.key === "F9") {
        setActivePedal("idle");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [currentState.kind, isLeftPedalF8]);

  const handleErrorClose = (): void => {
    if (currentState.kind !== "error") {
      return;
    }
    const expected = currentState.expected;
    setActivePedal("idle");
    setCurrentState({ kind: "initial" });
    if (recoveryTimerRef.current !== null) window.clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = window.setTimeout(
      () => setCurrentState({ kind: expected }),
      PRACTISE_DELAY_MS,
    );
  };

  React.useEffect(() => {
    if (currentState.kind !== "error") {
      return;
    }

    const handleEnterDismiss = (event: KeyboardEvent): void => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      handleErrorClose();
    };

    window.addEventListener("keydown", handleEnterDismiss);
    return () => {
      window.removeEventListener("keydown", handleEnterDismiss);
    };
  }, [currentState]);

  const hasCompletedLeft =
    currentState.kind === "right" ||
    currentState.kind === "finished" ||
    (currentState.kind === "error" && currentState.expected === "right");
  const expectedPedal: "left" | "right" =
    currentState.kind === "right" ||
    currentState.kind === "finished" ||
    (currentState.kind === "error" && currentState.expected === "right")
      ? "right"
      : "left";

  return (
    <DtgPractiseField hasError={currentState.kind === "error"} onErrorClose={handleErrorClose}>
      <Typography variant="h1" component="h1" color="var(--color-brand-primary-500)">
        Pedalen
      </Typography>
      <p>Druk het pedaal in dat overeen komt met de afbeelding.</p>

      <div
        className={style["playing-field-container"]}
        style={{ "--flow-padding": "6rem" } as React.CSSProperties}
      >
        <div className={style["playing-field"]}>
          <div className={style["pedal-field"]}>
            {expectedPedal === "left" ? (
              <div className={style["pedal-field-left"]}>
                <div
                  className={`${style["pedal-preview"]} ${activePedal === "left" ? style["pedal-preview-active"] : ""}`}
                >
                  L
                </div>
              </div>
            ) : (
              <div className={style["pedal-field-right"]}>
                <div
                  className={`${style["pedal-preview"]} ${activePedal === "right" ? style["pedal-preview-active"] : ""}`}
                >
                  R
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={style["sound-buttons-container"]}>
          {hasCompletedLeft ? (
            <PedalLeftSuccessIcon />
          ) : activePedal === "left" ? (
            <PedalLeftPressedIcon />
          ) : (
            <PedalLeftDefaultIcon />
          )}
          {currentState.kind === "finished" ? (
            <PedalRightSuccessIcon />
          ) : activePedal === "right" ? (
            <PedalRightPressedIcon />
          ) : (
            <PedalRightDefaultIcon />
          )}
        </div>
      </div>
    </DtgPractiseField>
  );
};
